/** App types (camelCase) for the Activity / Audit feed. */

import type { ActivitySource, ActivityCategory } from './activity-api';

export type { ActivitySource, ActivityCategory };

export interface ActivityItem {
  source: ActivitySource;
  activityId: string;
  activityType: string;
  description: string;
  actorUserId: string | null;
  actorName: string | null;
  projectId: string | null;
  projectName: string | null;
  taskId: string | null;
  taskCode: string | null;
  taskName: string | null;
  category: ActivityCategory;
  durationHours: number | null;
  /** See ActivityEvent.autoStopped. */
  autoStopped: boolean;
  autoStopReason: string | null;
  createdAt: string;
  activityDate: string;
}

export interface ActivityListResult {
  items: ActivityItem[];
  total: number;
  page: number;
  pageSize: number;
}

// ── My Activity dashboard (camelCase) ────────────────────────────────

export interface ActivityKpiDeltas {
  totalLoggedHours: number;
  tasksWorkedOn: number;
  tasksCreated: number;
  commentsAdded: number;
  tasksCompleted: number;
}

export interface ActivityKpis {
  totalLoggedHours: number;
  tasksWorkedOn: number;
  tasksCreated: number;
  commentsAdded: number;
  tasksCompleted: number;
  firstActivityAt: string | null;
  lastActivityAt: string | null;
  deltas: ActivityKpiDeltas;
}

export interface ActivityTimeDistributionItem {
  taskId: string | null;
  taskCode: string | null;
  taskName: string;
  hours: number;
  pct: number;
}

export interface ActivityTopItem {
  key: string;
  label: string;
  count: number;
  hours: number;
  pct: number;
}

export interface ActivityProductivityInsight {
  pctChange: number;
  direction: 'up' | 'down' | 'flat';
  message: string;
}

export interface ActivitySummary {
  date: string;
  kpis: ActivityKpis;
  timeDistribution: ActivityTimeDistributionItem[];
  topActivities: ActivityTopItem[];
  productivityInsight: ActivityProductivityInsight;
}

export interface ActivityEvent {
  activityId: string;
  source: ActivitySource;
  activityType: string;
  category: ActivityCategory;
  description: string;
  actorName: string | null;
  durationHours: number | null;
  /**
   * True when the SYSTEM performed the action rather than the actor — e.g. a
   * timer the force-archive stopped. The feed flags these so a stop nobody
   * clicked does not read as the user's own.
   */
  autoStopped: boolean;
  /** Why the system acted, e.g. `PROJECT_ARCHIVED`; null when it did not. */
  autoStopReason: string | null;
  createdAt: string;
}

export interface ActivityTaskGroup {
  taskId: string | null;
  taskCode: string | null;
  taskName: string | null;
  projectId: string | null;
  projectName: string | null;
  totalTimeHours: number;
  events: ActivityEvent[];
}

/** Dashboard tab filter — 'all' plus the backend categories shown as tabs. */
export type ActivityTab = 'all' | 'time_tracking' | 'task_action' | 'comment' | 'status_change';

export const ACTIVITY_CATEGORY_TABS: { value: ActivityTab; label: string }[] = [
  { value: 'all', label: 'All Activities' },
  { value: 'time_tracking', label: 'Time Tracking' },
  { value: 'task_action', label: 'Task Actions' },
  { value: 'comment', label: 'Comments' },
  { value: 'status_change', label: 'Status Changes' },
];

/** Selectable activity sources for the toolbar filter. */
export const ACTIVITY_SOURCE_OPTIONS: { value: ActivitySource; label: string }[] = [
  { value: 'task', label: 'Tasks' },
  { value: 'timesheet', label: 'Timesheets' },
  { value: 'module', label: 'Modules' },
  { value: 'feature', label: 'Features' },
];

/**
 * Curated activity-type codes for the toolbar filter (value = the backend
 * activity_type / action_type code). '' = all types.
 */
export const ACTIVITY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All activity' },
  // Tasks
  { value: 'TASK_CREATED', label: 'Task created' },
  { value: 'TASK_UPDATED', label: 'Task edited' },
  { value: 'STATUS_CHANGED', label: 'Task status changed' },
  { value: 'TASK_DELETED', label: 'Task deleted' },
  { value: 'MOVED_TO_PROJECT', label: 'Task moved to project' },
  { value: 'REMOVED_FROM_PROJECT', label: 'Task removed from project' },
  // Time tracking
  { value: 'TIME_STARTED', label: 'Clock in' },
  { value: 'TIME_STOPPED', label: 'Clock out' },
  { value: 'TIME_PAUSED', label: 'Timer paused' },
  { value: 'TIME_RESTARTED', label: 'Timer resumed' },
  { value: 'TIME_MANUALLY_LOGGED', label: 'Manual time entry' },
  // Comments & attachments
  { value: 'COMMENT_ADDED', label: 'Comment added' },
  { value: 'COMMENT_UPDATED', label: 'Comment edited' },
  { value: 'COMMENT_DELETED', label: 'Comment deleted' },
  { value: 'COMMENT_RESTORED', label: 'Comment restored' },
  { value: 'ATTACHMENT_ADDED', label: 'Attachment added' },
  { value: 'ATTACHMENT_DELETED', label: 'Attachment removed' },
  // Watchers
  { value: 'WATCHERS_ADDED', label: 'Watcher added' },
  { value: 'WATCHER_REMOVED', label: 'Watcher removed' },
  // Timesheets
  { value: 'SUBMITTED', label: 'Timesheet submitted' },
  { value: 'RESUBMITTED', label: 'Timesheet resubmitted' },
  { value: 'UNSUBMITTED', label: 'Timesheet unsubmitted' },
  { value: 'APPROVED', label: 'Timesheet approved' },
  { value: 'REJECTED', label: 'Timesheet rejected' },
];
